import type { ArchiveKind, ChatView, KnowledgeCreate, KnowledgeUpdate, PortalLang } from '@cc/api-types';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { liveIsUp } from './liveStatus';
import { pushToast } from './toast';

/** Every query key in one place, so live events and mutations invalidate the same things the screens read. */
export const qk = {
  me: ['me'] as const,
  dashboard: ['dashboard'] as const,
  leads: ['leads'] as const,
  leadAll: ['lead'] as const,
  lead: (id: number) => ['lead', id] as const,
  needsYou: ['needs-you'] as const,
  chats: ['chats'] as const,
  chatAll: ['chat'] as const,
  chat: (id: number) => ['chat', id] as const,
  bookings: ['bookings'] as const,
  bookingAll: ['booking'] as const,
  booking: (id: number) => ['booking', id] as const,
  freeGuidesAll: ['free-guides'] as const,
  freeGuides: (id: number) => ['free-guides', id] as const,
  calendarAll: ['calendar'] as const,
  calendar: (week: string) => ['calendar', week] as const,
  guides: ['guides'] as const,
  guideAll: ['guide'] as const,
  guide: (id: number) => ['guide', id] as const,
  settings: ['settings'] as const,
  setup: ['setup'] as const,
  hotels: ['hotels'] as const,
  guideSuggestions: ['guide-suggestions'] as const,
  knowledge: ['knowledge'] as const,
  archiveAll: ['settings', 'archive'] as const,
  archive: (kind: ArchiveKind | 'unsorted', offset: number) => ['settings', 'archive', kind, offset] as const,
  archiveChat: (id: number) => ['settings', 'archive', 'chat', id] as const,
  portalAll: (token: string) => ['portal', token] as const,
  portalHome: (token: string, lang: PortalLang) => ['portal', token, 'home', lang] as const,
  portalTrip: (token: string, id: number, lang: PortalLang) => ['portal', token, 'trip', id, lang] as const,
  portalMonth: (token: string, lang: PortalLang) => ['portal', token, 'month', lang] as const,
};

export const meQuery = queryOptions({ queryKey: qk.me, queryFn: ({ signal }) => api.me(signal) });
export const dashboardQuery = queryOptions({ queryKey: qk.dashboard, queryFn: ({ signal }) => api.dashboard(signal) });
export const leadsQuery = queryOptions({ queryKey: qk.leads, queryFn: ({ signal }) => api.leads(signal) });
export const leadQuery = (id: number) => queryOptions({ queryKey: qk.lead(id), queryFn: ({ signal }) => api.lead(id, signal) });
export const needsYouQuery = queryOptions({ queryKey: qk.needsYou, queryFn: ({ signal }) => api.needsYou(signal) });
export const chatsQuery = queryOptions({ queryKey: qk.chats, queryFn: ({ signal }) => api.chats(signal) });
/** A message of ours still on its way, sent in the last two minutes. */
const onItsWay = (chat: ChatView | undefined) =>
  !!chat?.messages.some((m) => m.status === 'queued' && Date.now() - Date.parse(m.at) < 120_000);
// Live events say when a message goes out; while one is on its way the thread also checks every few
// seconds, so a lost event can't leave "Sending…" on screen.
export const chatQuery = (id: number) =>
  queryOptions({
    queryKey: qk.chat(id),
    queryFn: ({ signal }) => api.chat(id, signal),
    refetchInterval: (q) => (onItsWay(q.state.data) ? 3_000 : liveIsUp() ? false : 30_000),
  });
export const bookingsQuery = queryOptions({ queryKey: qk.bookings, queryFn: ({ signal }) => api.bookings(signal) });
export const bookingQuery = (id: number) => queryOptions({ queryKey: qk.booking(id), queryFn: ({ signal }) => api.booking(id, signal) });
export const freeGuidesQuery = (id: number) =>
  queryOptions({ queryKey: qk.freeGuides(id), queryFn: ({ signal }) => api.freeGuides(id, signal), staleTime: 0 });
/** `week` is any day in the week; "current" asks the server for the week it considers current. */
export const calendarQuery = (week: string | undefined) =>
  queryOptions({ queryKey: qk.calendar(week ?? 'current'), queryFn: ({ signal }) => api.calendar(week, signal) });
export const guidesQuery = queryOptions({ queryKey: qk.guides, queryFn: ({ signal }) => api.guides(signal) });
export const guideQuery = (id: number) => queryOptions({ queryKey: qk.guide(id), queryFn: ({ signal }) => api.guide(id, signal) });
export const settingsQuery = queryOptions({ queryKey: qk.settings, queryFn: ({ signal }) => api.settings(signal) });
export const setupQuery = queryOptions({ queryKey: qk.setup, queryFn: ({ signal }) => api.setup(signal) });
export const knowledgeQuery = queryOptions({ queryKey: qk.knowledge, queryFn: ({ signal }) => api.knowledge(signal) });
export const hotelsQuery = queryOptions({ queryKey: qk.hotels, queryFn: ({ signal }) => api.hotels(signal) });
export const guideSuggestionsQuery = queryOptions({ queryKey: qk.guideSuggestions, queryFn: ({ signal }) => api.guideSuggestions(signal) });

export const portalHomeQuery = (token: string, lang: PortalLang) =>
  queryOptions({ queryKey: qk.portalHome(token, lang), queryFn: ({ signal }) => api.portal.home(token, lang, signal) });
export const portalTripQuery = (token: string, id: number, lang: PortalLang) =>
  queryOptions({ queryKey: qk.portalTrip(token, id, lang), queryFn: ({ signal }) => api.portal.trip(token, id, lang, signal) });
export const portalMonthQuery = (token: string, lang: PortalLang) =>
  queryOptions({ queryKey: qk.portalMonth(token, lang), queryFn: ({ signal }) => api.portal.month(token, lang, signal) });

export const archiveQuery = (kind: ArchiveKind | 'unsorted', offset: number) =>
  queryOptions({ queryKey: qk.archive(kind, offset), queryFn: ({ signal }) => api.archive(kind, offset, signal) });
export const archiveChatQuery = (id: number) => queryOptions({ queryKey: qk.archiveChat(id), queryFn: ({ signal }) => api.archiveChat(id, signal) });

/** What Seth adds to Keta's knowledge by hand. */
export function useAddKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: KnowledgeCreate) => api.addKnowledge(body),
    onSuccess: () => pushToast('ok', 'Added. Keta knows it from the next message.'),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.knowledge }),
  });
}

/** One change to a thing Keta knows. `done` is the toast for a change that worked. */
export interface KnowledgeChange {
  id: number;
  body: KnowledgeUpdate;
  done?: string;
}

/** Edit, accept, retire or restore one thing Keta knows. */
export function useUpdateKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: KnowledgeChange) => api.updateKnowledge(id, body),
    onSuccess: (_entry, v) => pushToast('ok', v.done ?? 'Saved.'),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.knowledge }),
  });
}
